import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import api from '../api/client';
import { Tray, CheckCircle, XCircle, UserPlus, PencilSimple } from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

function Submissions() {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/submissions');
      setSubmissions(res.data);
    } catch (error) {
      toast.error('Failed to load submissions');
    } finally {
      setLoading(false);
    }
  };

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/admin/submissions/${id}/approve`);
      toast.success('Submission approved');
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    setBusyId(id);
    try {
      await api.delete(`/admin/submissions/${id}`);
      toast.success('Submission removed');
      setSubmissions((prev) => prev.filter((s) => s.id !== id));
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Layout>
      <div className="px-6 sm:px-8 lg:px-12 py-8" data-testid="submissions-page">
        <div className="flex items-center gap-3 mb-2">
          <Tray size={28} weight="duotone" className="text-blue-400" />
          <h1 className="text-3xl font-black tracking-tight text-white">Submissions</h1>
        </div>
        <p className="text-sm text-slate-400 mb-8">
          Registrations and update requests from the public form, awaiting your review.
        </p>

        {loading ? (
          <p className="text-slate-400" data-testid="submissions-loading">Loading...</p>
        ) : submissions.length === 0 ? (
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-12 text-center" data-testid="submissions-empty">
            <Tray size={40} weight="duotone" className="text-slate-600 mx-auto mb-3" />
            <p className="text-slate-400">No pending submissions right now.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="submissions-list">
            {submissions.map((s) => (
              <div key={s.id} data-testid={`submission-${s.id}`} className="bg-slate-900 border border-slate-800 rounded-xl p-5">
                <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      {s.submission_type === 'update' ? (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-400">
                          <PencilSimple size={12} weight="bold" /> Update
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-blue-500/10 text-blue-400">
                          <UserPlus size={12} weight="bold" /> New
                        </span>
                      )}
                      <h3 className="text-lg font-bold text-white">{s.name}</h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-1 text-sm">
                      <Detail label="Phone" value={s.phone} />
                      <Detail label="Batch" value={s.batch_name || '—'} />
                      <Detail label="DOB" value={s.date_of_birth || '—'} />
                      <Detail label="Email" value={s.email || '—'} />
                      <Detail label="Parent" value={s.parent_name || '—'} />
                      <Detail label="Parent Phone" value={s.parent_phone || '—'} />
                    </div>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Button
                      onClick={() => approve(s.id)}
                      disabled={busyId === s.id}
                      data-testid={`approve-${s.id}`}
                      className="bg-green-600 hover:bg-green-500 text-white"
                    >
                      <CheckCircle size={16} weight="duotone" className="mr-1.5" /> Approve
                    </Button>
                    <Button
                      onClick={() => reject(s.id)}
                      disabled={busyId === s.id}
                      data-testid={`reject-${s.id}`}
                      variant="outline"
                      className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                    >
                      <XCircle size={16} weight="duotone" className="mr-1.5" /> Reject
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
}

const Detail = ({ label, value }) => (
  <div>
    <span className="text-slate-500">{label}: </span>
    <span className="text-slate-200">{value}</span>
  </div>
);

export default Submissions;
